/**
 * The one thing that decides when a player is interrupted.
 *
 * Toasts, the badge, the inbox and the live feed all hang off here, because
 * the interesting rules are about TIMING and timing cannot be decided by any
 * one of them alone: whether to show a toast depends on what screen the player
 * is on, what they have already been told about that challenge, and whether
 * the screen they are looking at already says it.
 */

import { Feed } from '@/net/notify';
import { syncAppBadge, sweepTray, seenPlan, type BadgeNav } from '@/net/badge';
import { Inbox, Toasts } from '@/ui/notify-ui';
import type { AppNotification } from '@/net/api';
import { changesMyChallenges } from '@/net/challenges-changed';

/** Between released toasts after a round. */
const FLUSH_GAP_MS = 1200;

export interface NotifyHostCallbacks {
  onOpenChallenge(challengeId: string): void;
  /** A chest notice opens the chest it is about. */
  onOpenChest?(chestId: string): void;
  /**
   * A chest notice the chest list has not heard about: from the live stream, or found by a foreground re-read
   * (the stream may have been dead while the app was away). The host re-reads the chest list, so the pill counts
   * it and, on home, its arrival plays; without this only the unread dot moved (Blockari 3f31ff7).
   */
  onChestNews?(): void;
  /**
   * A notice that one of the player's challenges MOVED arrived (taken, won, lost, expired, voided) — by the stream, or
   * found by a foreground re-read, read or not — whether or not a toast is held back for the round or the challenge's
   * own page is up. Every list of the player's own challenges is stale after one; the host re-reads whichever is open.
   */
  onChallengesChanged?(): void;
  onProfile(): void;
  onLeaderboard?(): void;
  onGuide?(): void;
  onSettings?(): void;
  /**
   * The unread count changed.
   *
   * Reported rather than rendered here: every screen builds its own top bar,
   * so there are seven avatars to put a number on and one DOM node cannot be
   * in seven places. The caller fans it out, exactly as it does the account.
   */
  onUnread(count: number): void;
  /** A win or an expiry moved money; the pill should tween to the new total. */
  onBalanceDelta(amount: number, n: AppNotification): void;
  /** A toast is on screen (its coin, if it pays one, can leave now). */
  onToastShown?(n: AppNotification, toast: HTMLElement): void;
}

export class NotifyHost {
  readonly toasts: Toasts;
  readonly inbox: Inbox;

  readonly #feed: Feed;
  readonly #callbacks: NotifyHostCallbacks;

  /** Everything known, newest first. The inbox renders from this. */
  #items: AppNotification[] = [];

  /**
   * Held back because the player is mid-round.
   *
   * Keyed by challenge, so a challenge that is taken and then settles while
   * the player is still playing releases ONE toast — the settlement — rather
   * than two describing the same story at different stages.
   */
  readonly #queued = new Map<string, AppNotification>();

  /** True while a round is in progress: count-in, play, pause, the lot. */
  #inRound = false;
  /** The challenge whose own screen is open, if any. */
  #onScreen: string | null = null;
  #flushTimer = 0;

  constructor(callbacks: NotifyHostCallbacks) {
    this.#callbacks = callbacks;
    this.toasts = new Toasts({
      onOpen: (id) => callbacks.onOpenChallenge(id),
      onOpenChest: (id) => callbacks.onOpenChest?.(id),
      onShown: (n, toast) => callbacks.onToastShown?.(n, toast),
    });
    this.inbox = new Inbox({
      onOpen: (id) => callbacks.onOpenChallenge(id),
      onOpenChest: (id) => callbacks.onOpenChest?.(id),
      onMarkRead: (ids) => void this.#markRead(ids),
      onProfile: () => callbacks.onProfile(),
      onLeaderboard: () => callbacks.onLeaderboard?.(),
      onGuide: () => callbacks.onGuide?.(),
      onSettings: () => callbacks.onSettings?.(),
      onAll: () => this.#showAll(),
    });
    this.#feed = new Feed({
      onNotification: (n) => this.#arrived(n),
      onUnread: (count) => {
        callbacks.onUnread(count);
        this.#badge();
        if (this.inbox.isOpen) this.inbox.syncUnread(count);
      },
      // The foreground re-read: what the server says is read IS read, whichever device read it.
      onResync: (items) => {
        // The first load is the session's own chest read; after it, an unread chest row not yet known is news.
        const known = new Set(this.#items.map((i) => i.id));
        const chestNews =
          this.#loaded && items.some((i) => i.kind === 'chest_earned' && i.readAt === null && !known.has(i.id));
        // A challenge that moved while the stream could not say so (it was down, or the app was away), found by the re-read:
        // read or not (another device may have read it), the lists are stale all the same.
        if (this.#loaded && items.some((i) => changesMyChallenges(i.kind) && !known.has(i.id))) callbacks.onChallengesChanged?.();
        this.#items = items;
        this.#badge();
        if (this.inbox.isOpen) this.inbox.show(this.#items, this.#feed.unread);
        if (chestNews) callbacks.onChestNews?.();
      },
    });
    window.addEventListener('resize', () => this.toasts.measure());
  }

  /** Sign-in. Loads what is waiting and opens the live stream. */
  async start(): Promise<void> {
    this.#loaded = false;
    this.#items = await this.#feed.start();
    this.#loaded = true;
    this.#callbacks.onUnread(this.#feed.unread);
    this.#badge();
    if (this.inbox.isOpen) this.inbox.show(this.#items, this.#feed.unread);
  }

  /** False until the first fetch has answered; the inbox says "Loading…" until then. */
  #loaded = false;

  /** Sign-out. Everything about the previous account goes with it. */
  stop(): void {
    this.#feed.stop();
    window.clearTimeout(this.#flushTimer);
    this.#queued.clear();
    this.#items = [];
    this.toasts.clear();
    this.inbox.close();
    this.#callbacks.onUnread(0);
    // Signed out: nothing to badge, and nothing in the tray.
    void syncAppBadge(0, navigator as BadgeNav);
    void this.#registration().then((reg) => sweepTray(reg, [], 0));
  }

  async #registration(): Promise<ServiceWorkerRegistration | undefined> {
    if (!('serviceWorker' in navigator)) return undefined;
    try {
      return await navigator.serviceWorker.getRegistration('/sw.js');
    } catch {
      return undefined;
    }
  }

  /**
   * The home-screen badge and the notification tray follow ONE number, the server's unread count:
   * the icon badge is set to it (cleared at zero) and, while the app is visible, tray entries whose
   * rows are read are closed (on Android the launcher dot counts the tray). See `net/badge.ts`.
   */
  #badge(): void {
    if (!this.#loaded) return;
    const unread = this.#feed.unread;
    void syncAppBadge(unread, navigator as BadgeNav);
    if (document.visibilityState === 'visible') {
      void this.#registration().then((reg) => sweepTray(reg, this.#items, unread));
    }
  }

  openInbox(): void {
    if (this.inbox.isOpen) this.inbox.close();
    else this.inbox.open(this.#items, this.#feed.unread, this.#loaded);
  }

  /**
   * Which screen the player is on.
   *
   * `inRound` covers the whole round rather than just active play — the
   * count-in and a pause are still the round, and a toast over a paused board
   * is a toast over a board the player is about to come back to.
   *
   * `challengeId` is set when a screen is ABOUT one challenge: its own page,
   * or the results of the round just played. Anything about that challenge is
   * then marked read silently, because the screen already says it and a toast
   * repeating it is noise.
   */
  setContext(context: { inRound: boolean; challengeId?: string | null }): void {
    const wasInRound = this.#inRound;
    this.#inRound = context.inRound;
    this.#onScreen = context.challengeId ?? null;

    if (this.#onScreen) {
      // The screen says it, so nothing else should: its settlement — which
      // arrived during the round and was queued, before this screen knew
      // which challenge it was about — leaves the queue and the stack here,
      // not only the unread list. Left in the queue, "You lost" popped on
      // home the moment the player left results.
      this.#queued.delete(this.#onScreen);
      this.toasts.drop(this.#onScreen);
      void this.#readChallenge(this.#onScreen);
    }
    // The round ended: release what was held back.
    if (wasInRound && !context.inRound) this.#flush();
  }

  #arrived(n: AppNotification): void {
    this.#items = [n, ...this.#items.filter((item) => item.id !== n.id)];
    // A chest earned: the pill counts it at once, whatever the toast does (Blockari's stream handler).
    if (n.kind === 'chest_earned') this.#callbacks.onChestNews?.();
    // A challenge moved: whatever list of the player's own challenges is open is a snapshot from before it. Raised
    // before the early returns below: the challenge's own page being up does not refresh the OTHER lists.
    if (changesMyChallenges(n.kind)) this.#callbacks.onChallengesChanged?.();

    // The screen the player is looking at is already about this challenge.
    // (A chest belongs to no challenge, so it is never already on screen.)
    if (n.challengeId !== null && this.#onScreen === n.challengeId) {
      void this.#readChallenge(n.challengeId);
      this.#applyBalance(n);
      return;
    }

    if (this.#inRound) {
      // Latest wins. Two notifications about one challenge are one story; a
      // chest is its own, so it queues under its own id.
      this.#queued.set(n.challengeId ?? n.chestId ?? n.id, n);
      return;
    }

    this.#applyBalance(n);
    this.toasts.show(n);
    if (this.inbox.isOpen) this.inbox.show(this.#items, this.#feed.unread);
  }

  /**
   * Release the backlog one at a time, newest first.
   *
   * All of them at once reads as an error rather than as news — three results
   * arriving in the same frame looks like the app catching up on something
   * that went wrong. Newest first because that is the one the player most
   * wants, and the stack only holds three.
   */
  #flush(): void {
    // Anything read meanwhile — by the screen it was about, or by the inbox
    // — is old news, and news the player is looking at is not toasted.
    const pending = [...this.#queued.values()]
      .filter((n) => n.challengeId !== this.#onScreen)
      .filter((n) => this.#items.find((item) => item.id === n.id)?.readAt == null)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    this.#queued.clear();
    if (pending.length === 0) return;

    let index = 0;
    const next = (): void => {
      const n = pending[index++];
      if (!n) return;
      this.#applyBalance(n);
      this.toasts.show(n);
      if (index < pending.length) this.#flushTimer = window.setTimeout(next, FLUSH_GAP_MS);
    };
    next();
  }

  /**
   * Move the balance pill without waiting for a round trip.
   *
   * The notification already carries the delta, so the number can move the
   * moment the player is told. The next `/auth/me` reconciles — and lands on
   * the same value, which must not re-tween.
   */
  #applyBalance(n: AppNotification): void {
    if (n.kind !== 'challenge_won' && n.kind !== 'challenge_expired' && n.kind !== 'challenge_voided') return;
    if (n.amount === 0) return;
    this.#callbacks.onBalanceDelta(n.amount, n);
  }

  async #markRead(ids: readonly string[] | undefined): Promise<void> {
    const marked = new Set(ids);
    const now = new Date().toISOString();
    this.#items = this.#items.map((item) =>
      item.readAt === null && (ids === undefined || marked.has(item.id))
        ? { ...item, readAt: now }
        : item,
    );
    await this.#feed.markRead(ids);
    this.#badge();
    // "Mark all read" disappears once nothing is unread, as Blockari's does (rows keep their rails).
    if (this.inbox.isOpen) this.inbox.syncUnread(this.#feed.unread);
    // The panel keeps its left rules while it is open — see Inbox.open — so it
    // is deliberately NOT re-rendered here.
  }

  async #readChallenge(challengeId: string): Promise<void> {
    const unread = this.#items.filter((n) => n.challengeId === challengeId && n.readAt === null);
    if (unread.length === 0) return;
    await this.#markRead(unread.map((n) => n.id));
  }

  #showAll(): void {
    // No router here: every screen is a panel toggled by a data attribute, and
    // a link is a query string. The inbox's own list already holds everything
    // the server returned, so "all" opens the same panel scrolled to the top
    // rather than a second copy of it on a route that would need inventing.
    this.inbox.open(this.#items, this.#feed.unread, this.#loaded);
  }
}
